// ─────────────────────────────────────────────────────────────
// RSVPPage.js
// Public-facing page shell. Loads settings, content, locations.
// Passes config down to EventCalendar and CommentThread.
// ─────────────────────────────────────────────────────────────

import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import {
  collection, addDoc, getDocs, Timestamp, updateDoc, doc, query, limit, where, orderBy, writeBatch
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { useSearchParams } from 'react-router-dom';
import EventCalendar from './EventCalendar';
import CommentThread from './CommentThread';
import { generateSeriesICS, downloadICS } from '../utils/calendarUtils';
import RecentActivity from './RecentActivity';

function RSVPPage() {

  const [searchParams] = useSearchParams();
  const deepLinkedEventId = searchParams.get('event') || null;

  // ─── 1. STATE ───────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [commentCount, setCommentCount] = useState(null);

  // Admin auth
  const [adminUser, setAdminUser] = useState(null);

  // Locations
  const [locations, setLocations] = useState({});

  // Settings
  const [showWeather, setShowWeather] = useState(true);
  const [showDiscussion, setShowDiscussion] = useState(true);
  const [disclaimer, setDisclaimer] = useState('');
  const [minPlayers, setMinPlayers] = useState(8);

  // Content sections
  const [contentTop, setContentTop] = useState('');
  const [contentBottom1, setContentBottom1] = useState('');
  const [contentBottom2, setContentBottom2] = useState('');
  const [contentBottom3, setContentBottom3] = useState('');
  const [contentBottom4, setContentBottom4] = useState('');

  // Toast notifications
  const [toasts, setToasts] = useState([]);

  // ─── 2. EFFECTS ─────────────────────────────────────────────
  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, user => {
      setAdminUser(user || null);
    });
    loadLocations();
    loadContent();
    loadSettings();
    loadLastAttendance();
    return () => unsubAuth();
  }, []);

  // ─── 3. TOAST HELPER ────────────────────────────────────────
  // Error toasts are also written to the audit log with browser/device
  // clues — error toasts are rare and usually un-reproducible by us, so
  // capturing context at the moment they happen is the only practical way
  // to diagnose them later (see: fingerprint/Brave RSVP-update bug).
  const showToast = (message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3000);

    if (type === 'error') {
      addDoc(collection(db, 'auditLogs'), {
        actorType: 'user',
        actorId: localStorage.getItem('rsvp_device_id') || null,
        actorName: localStorage.getItem('rsvp_user_name') || null,
        action: 'client_error_toast',
        entityType: 'client',
        entityId: null,
        entityLabel: message,
        before: null,
        after: null,
        metadata: {
          message,
          url: window.location.href,
          userAgent: navigator.userAgent,
          deviceId: localStorage.getItem('rsvp_device_id') || null,
        },
        timestamp: Timestamp.now(),
      }).catch(e => console.warn('Could not log error toast:', e));
    }
  };

  // ─── 4. DATA LOADING ────────────────────────────────────────
  const loadLocations = async () => {
    try {
      const snap = await getDocs(collection(db, 'locations'));
      const map = {};
      snap.docs.forEach(d => { map[d.id] = { id: d.id, ...d.data() }; });
      setLocations(map);
      setLoading(false);
    } catch (error) {
      console.error('Error loading locations:', error);
      setLoading(false);
    }
  };

  const loadLastAttendance = async () => {
    try {
      const now = new Date();
      const snap = await getDocs(
        query(
          collection(db, 'events'),
          where('date', '<', Timestamp.fromDate(now)),
          orderBy('date', 'desc'),
          limit(20)  // fetch more so we can find up to 8 with actualAttendance recorded
        )
      );
      const allPastDocs = snap.docs.map(d => d.data());
      // Detect if the most recent past event was canceled (recency guard for [bestAttendance])
      setLastGameWasCanceled(allPastDocs[0]?.isCanceled === true);

      const pastWithAttendance = allPastDocs
        .filter(e => e.actualAttendance != null)
        .slice(0, 8);  // cap at 8 for stats

      // [DateOfLastGame] — most recent
      const recent = pastWithAttendance[0];
      if (recent) {
        const date = recent.date.toDate();
        const dateStr = date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
        const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'long' });
        setLastAttendance({ count: recent.actualAttendance, dateStr, dayOfWeek });
      }

      // Compute attendance stats for content variables
      const avg = (arr) => arr.length
        ? Math.round(arr.reduce((s, e) => s + e.actualAttendance, 0) / arr.length)
        : null;

      // [averagePlayersThisMonth] — most recent 4 games
      const avg4 = avg(pastWithAttendance.slice(0, 4));

      // [averagePlayersLast6weeks] — most recent 6 games
      const avg6 = avg(pastWithAttendance.slice(0, 6));

      // [recentAttendance] / [recentAttendanceWeeks] — best average over 2–8 consecutive games
      let bestAvg = null;
      let bestWeeks = null;
      for (let w = 2; w <= Math.min(8, pastWithAttendance.length); w++) {
        const a = avg(pastWithAttendance.slice(0, w));
        if (a !== null && (bestAvg === null || a > bestAvg)) {
          bestAvg = a;
          bestWeeks = w;
        }
      }

      setAttendanceStats({ avg4, avg6, recentAvg: bestAvg, recentWeeks: bestWeeks });
    } catch (e) { console.error('loadLastAttendance:', e); }
  };

  const loadSettings = async () => {
    try {
      const snap = await getDocs(query(collection(db, 'settings'), limit(1)));
      if (!snap.empty) {
        const s = snap.docs[0].data();
        setShowWeather(s.showWeather !== false);
        setShowDiscussion(s.showDiscussion !== false);
        setDisclaimer(s.disclaimer || '');
        setSiteUrl(s.siteUrl || '');
        setSiteName(s.siteName || '');
        setContactEmail(s.contactEmail || '');
        setFacebookUrl(s.facebookUrl || '');
        setInstagramUrl(s.instagramUrl || '');
        setHeroImageUrl(s.heroImageUrl || '');
        setStatusLines(s.statusLines || []);
        setMinPlayers(s.minPlayers || 8);
        if (s.bestAttendancePhraseLastGame) setBestAttendancePhraseLastGame(s.bestAttendancePhraseLastGame);
        if (s.bestAttendancePhraseRecent)   setBestAttendancePhraseRecent(s.bestAttendancePhraseRecent);
        if (s.recurringEvent) {
          setRecurringSettings(s.recurringEvent);
          setCalendarDescription(s.recurringEvent.calendarDescription || '');
        }
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const loadContent = async () => {
    try {
      const snap = await getDocs(collection(db, 'content'));
      if (!snap.empty) {
        const c = snap.docs[0].data();
        setContentTop(c.top2 || '');
        setContentBottom1(c.bottom1 || '');
        setContentBottom2(c.bottom2 || '');
        // bottom3/bottom4 are intentionally swapped: the CMS stores them in
        // creation order but they render in reverse order in the UI.
        setContentBottom3(c.bottom4 || '');
        setContentBottom4(c.bottom3 || '');

      }
    } catch (error) {
      console.error('Error loading content:', error);
    }
  };

  // Callback ref — EventCalendar registers this so RSVPPage can trigger a refresh
  const [refreshEventFn, setRefreshEventFn] = useState(null);
  // Title and next-event flag lifted from EventCalendar for the header
  const [lastAttendance, setLastAttendance] = useState(null);
  const [lastGameWasCanceled, setLastGameWasCanceled] = useState(false);
  const [attendanceStats, setAttendanceStats] = useState({ avg4: null, avg6: null, recentAvg: null, recentWeeks: null });
  const [bestAttendancePhraseLastGame, setBestAttendancePhraseLastGame] = useState('[N] played last time we hit the field');
  const [bestAttendancePhraseRecent, setBestAttendancePhraseRecent] = useState('[N] players on average recently');
  const [siteUrl, setSiteUrl] = useState('');
  const [heroImageUrl, setHeroImageUrl] = useState('');
  const [siteName, setSiteName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [facebookUrl, setFacebookUrl] = useState('');
  const [instagramUrl, setInstagramUrl] = useState('');
  const [statusLines, setStatusLines] = useState([]);
  const [recurringSettings, setRecurringSettings] = useState(null);
  const [calendarDescription, setCalendarDescription] = useState('');

  // ─── CONTENT VARIABLE RESOLVER ──────────────────────────────
  const resolveContentVars = (html) => {
    if (!html) return html;
    const { avg4, avg6, recentAvg, recentWeeks } = attendanceStats;

    // [bestAttendance]: a strong last game (>= 14) is used as-is; below that, pick the
    // more flattering of last game vs recent average (ties prefer last game).
    // Suppress the last-game stat if that event was canceled.
    const bestAttendance = (() => {
      const STRONG_TURNOUT = 14;
      const lastCount = (!lastGameWasCanceled && lastAttendance?.count != null)
        ? lastAttendance.count : null;
      let winner = null;
      if (lastCount != null) {
        winner = { value: lastCount, template: bestAttendancePhraseLastGame };
      }
      if ((lastCount == null || lastCount < STRONG_TURNOUT) &&
          recentAvg != null && (winner == null || recentAvg > winner.value)) {
        winner = { value: recentAvg, template: bestAttendancePhraseRecent };
      }
      return winner ? winner.template.replace(/\[N\]/g, String(winner.value)) : '—';
    })();

    return html
      .replace(/\[DateOfLastGame\]/g,
        lastAttendance?.dateStr
          ? `${lastAttendance.dayOfWeek}, ${lastAttendance.dateStr}`
          : '—'
      )
      .replace(/\[PlayersLastGame\]/g,
        lastAttendance?.count != null ? String(lastAttendance.count) : '—'
      )
      .replace(/\[averagePlayersThisMonth\]/g,
        avg4 != null ? String(avg4) : '—'
      )
      .replace(/\[averagePlayersLast6weeks\]/g,
        avg6 != null ? String(avg6) : '—'
      )
      .replace(/\[recentAttendance\]/g,
        recentAvg != null ? String(recentAvg) : '—'
      )
      .replace(/\[recentAttendanceWeeks\]/g,
        recentWeeks != null ? String(recentWeeks) : '—'
      )
      .replace(/\[bestAttendance\]/g, bestAttendance);
  };
  const handleAdminConfirm = async (eventId, currentStatus, entityLabel = null) => {
    try {
      const batch = writeBatch(db);
      batch.update(doc(db, 'events', eventId), {
        isConfirmed: !currentStatus,
        isCanceled: false
      });
      batch.set(doc(collection(db, 'auditLogs')), {
        actorType: 'admin',
        actorId: adminUser.email,
        actorName: adminUser.email,
        action: currentStatus ? 'event_unconfirmed' : 'event_confirmed',
        entityType: 'event',
        entityId: eventId,
        entityLabel,
        before: { isConfirmed: currentStatus },
        after: { isConfirmed: !currentStatus, isCanceled: false },
        timestamp: Timestamp.now()
      });
      await batch.commit();
      if (refreshEventFn) refreshEventFn(eventId);
    } catch (error) { showToast('Error updating event status', 'error'); }
  };

  const handleAdminCancel = async (eventId, currentStatus, entityLabel = null) => {
    try {
      const batch = writeBatch(db);
      batch.update(doc(db, 'events', eventId), {
        isCanceled: !currentStatus,
        isConfirmed: false
      });
      batch.set(doc(collection(db, 'auditLogs')), {
        actorType: 'admin',
        actorId: adminUser.email,
        actorName: adminUser.email,
        action: currentStatus ? 'event_uncanceled' : 'event_canceled',
        entityType: 'event',
        entityId: eventId,
        entityLabel,
        before: { isCanceled: currentStatus },
        after: { isCanceled: !currentStatus, isConfirmed: false },
        timestamp: Timestamp.now()
      });
      await batch.commit();
      if (refreshEventFn) refreshEventFn(eventId);
    } catch (error) { showToast('Error updating event status', 'error'); }
  };

  const handleAdminSaveAttendance = async (eventId, value, entityLabel = null) => {
    try {
      const val = parseInt(value) || 0;
      await updateDoc(doc(db, 'events', eventId), {
        actualAttendance: val,
        updatedAt: Timestamp.now()
      });
      await addDoc(collection(db, 'auditLogs'), {
        actorType: 'admin',
        actorId: adminUser.email,
        actorName: adminUser.email,
        action: 'attendance_updated',
        entityType: 'event',
        entityId: eventId,
        entityLabel,
        before: null,
        after: { actualAttendance: val },
        timestamp: Timestamp.now()
      });
      showToast('Attendance saved!');
      if (refreshEventFn) refreshEventFn(eventId);
    } catch (error) { showToast('Error saving attendance', 'error'); }
  };

  // ─── 6. RENDER ──────────────────────────────────────────────
  if (loading) {
    return (
      <div className="container">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <>
      {/* ── HERO IMAGE HEADER ── */}
      {heroImageUrl && (
        <div
          className="site-hero"
          style={{backgroundImage: `url(${heroImageUrl})`}}
        >
          <div className="site-hero-gradient" />
          {siteName && (
            <div className="site-hero-title">{siteName}</div>
          )}
        </div>
      )}

      <div className="container">
        {contentTop && (
          <div className="content-section top"
            dangerouslySetInnerHTML={{ __html: resolveContentVars(contentTop) }} />
        )}

        {/* ── EVENT CARD — new design ── */}
        <div className="evt-unified-card">
        <EventCalendar
          locations={locations}
          adminUser={adminUser}
          showToast={showToast}
          onConfirm={handleAdminConfirm}
          onCancel={handleAdminCancel}
          onSaveAttendance={handleAdminSaveAttendance}
          onRegisterRefresh={fn => setRefreshEventFn(() => fn)}
          lastAttendance={lastAttendance}
          showWeather={showWeather}
          disclaimer={disclaimer}
          siteUrl={siteUrl}
          minPlayers={minPlayers}
          statusLines={statusLines}
          recurringSettings={recurringSettings}
          calendarDescription={calendarDescription}
          deepLinkedEventId={deepLinkedEventId}
        />
        </div>

        <div className="bottom-content-sections">
          {contentBottom1 && (
            <div className="content-section bottom"
              dangerouslySetInnerHTML={{ __html: resolveContentVars(contentBottom1) }} />
          )}

          {/* About Us — preview/rest split by <!--more--> marker, or first <p> as fallback */}
          {contentBottom2 && (() => {
            const MORE_MARKER = '<!--more-->';
            let preview, rest;
            if (contentBottom2.includes(MORE_MARKER)) {
              const idx = contentBottom2.indexOf(MORE_MARKER);
              preview = contentBottom2.slice(0, idx);
              rest = contentBottom2.slice(idx + MORE_MARKER.length);
            } else {
              const firstPMatch = contentBottom2.match(/<p[^>]*>([\s\S]*?)<\/p>/i);
              const firstP = firstPMatch ? firstPMatch[0] : '';
              preview = firstP || contentBottom2;
              rest = firstP ? contentBottom2.replace(firstP, '') : '';
            }
            const hasMore = rest.replace(/<[^>]+>/g, '').trim().length > 0;
            return (
              <>
                <div className="section-bar">
                  <span>About Us</span>
                </div>
                <div className="section-bar-body">
                  <div dangerouslySetInnerHTML={{ __html: resolveContentVars(preview) }} />
                  {/* Rest expandable */}
                  {hasMore && (
                    <>
                      {aboutOpen && (
                        <div dangerouslySetInnerHTML={{
                          __html: resolveContentVars(rest)
                        }} />
                      )}
                      <button
                        className="evt-desc-toggle"
                        style={{
                          fontSize:'15px', marginTop:'0.5rem', display:'inline-block',
                          color:'var(--blue)', fontWeight:600,
                          padding:'0 0 1px',
                          background:'none', border:'none',
                          borderBottom:'1px solid var(--blue)',
                          cursor:'pointer', fontFamily:'inherit',
                        }}
                        onClick={() => setAboutOpen(prev => !prev)}>
                        {aboutOpen ? 'Less ▴' : 'Read more ▾'}
                      </button>
                    </>
                  )}
                </div>
              </>
            );
          })()}

          {/* Questions — standalone, independent of the Chat/Discussion
              toggle. Chat saw zero use in two months and has been hidden;
              regulars already use Facebook, so this is the contact point
              for everyone else (mainly first-timers without Facebook).
              contentBottom3 is just the intro line — the "Email us" and
              "Join us on Facebook" buttons are generated automatically
              from the contactEmail/facebookUrl settings, so admins never
              need to hand-write any link HTML. */}
          {(contentBottom3 || contactEmail || facebookUrl || instagramUrl || recurringSettings?.enabled) && (
            <>
              <div className="section-bar">
                <span>Stay Connected</span>
              </div>
              <div className="section-bar-body">
                {contentBottom3 && (
                  <div dangerouslySetInnerHTML={{ __html: resolveContentVars(contentBottom3) }} />
                )}
                {(contactEmail || facebookUrl || instagramUrl || recurringSettings?.enabled) && (
                  <div className="social-icon-row">
                    {contactEmail && (
                      <a href={`mailto:${contactEmail}`} className="social-icon-link social-icon-email" title="Email us" aria-label="Email us">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="2" y="4" width="20" height="16" rx="2"/>
                          <path d="m2 7 10 7 10-7"/>
                        </svg>
                      </a>
                    )}
                    {recurringSettings?.enabled && (
                      <button
                        className="social-icon-link social-icon-calendar"
                        title="Subscribe to schedule"
                        aria-label="Subscribe to schedule"
                        onClick={() => {
                          const ics = generateSeriesICS(recurringSettings, locations[recurringSettings?.defaultLocationId] || null, siteUrl, calendarDescription);
                          if (ics) { downloadICS(ics, 'pickup-ultimate-schedule.ics'); showToast('Open the file to import. Google Calendar: Other calendars → Import'); }
                        }}
                      >
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19 3h-1V1h-2v2H8V1H6v2H5C3.9 3 3 3.9 3 5v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
                        </svg>
                      </button>
                    )}
                    {facebookUrl && (
                      <a href={facebookUrl} target="_blank" rel="noopener noreferrer" className="social-icon-link social-icon-facebook" title="Facebook" aria-label="Facebook">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
                        </svg>
                      </a>
                    )}
                    {instagramUrl && (
                      <a href={instagramUrl} target="_blank" rel="noopener noreferrer" className="social-icon-link social-icon-instagram" title="Instagram" aria-label="Instagram">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                          <circle cx="12" cy="12" r="4"/>
                          <circle cx="17.5" cy="6.5" r="1.5" fill="currentColor" stroke="none"/>
                        </svg>
                      </a>
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Recent Activity — always-populated replacement for the old
              standalone "Chat" section, which saw zero use in two months.
              RSVPs happen every week regardless of whether anyone posts a
              comment, so this never looks dead the way an empty comment
              thread did. The comment thread (admin-toggleable via
              showDiscussion) is now a secondary "Ask a question" zone
              attached directly below it in the same card, rather than its
              own top-level section — kept visually distinct so a real
              question doesn't get lost among routine RSVP activity. */}
          <div className="section-bar">
            <span>Recent Activity</span>
          </div>
          <div className="section-bar-body" style={showDiscussion ? { borderBottomLeftRadius: 0, borderBottomRightRadius: 0, borderBottom: 'none' } : undefined}>
            <RecentActivity />
          </div>
          {showDiscussion && (
            <>
              <div className="recent-activity-ask-divider">
                <span>Ask a question</span>
                {commentCount > 0 && (
                  <span style={{fontWeight:400, textTransform:'none', letterSpacing:0}}>
                    {commentCount} {commentCount === 1 ? 'comment' : 'comments'}
                  </span>
                )}
              </div>
              <CommentThread
                adminUser={adminUser}
                showToast={showToast}
                onCountChange={setCommentCount}
              />
            </>
          )}

          {contentBottom4 && (
            <div className="content-section bottom"
              dangerouslySetInnerHTML={{ __html: resolveContentVars(contentBottom4) }} />
          )}
        </div>

        {/* Toast notifications */}
        <div className="toast-container">
          {toasts.map(t => (
            <div key={t.id} className={`toast toast-${t.type}`}>{t.message}</div>
          ))}
        </div>
      </div>
    </>
  );
}

export default RSVPPage;
